import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Box,
  FileCode,
  Grid,
  Package,
  Plus,
  Search,
  Settings,
  Upload,
  Zap,
  X,
} from 'lucide-react';
import { EmptyState, Spinner, useToast } from '@centraoneplus/ui';
import { useCapabilityCatalog, useDeleteCapability, useUpdateCapability } from '@hooks/useIntegrations';
import type { Capability } from '@services/integrations.service';
import { CapabilityCard } from './components/CapabilityCard';
import { ImportWizard } from './components/ImportWizard';
import { CapabilityEditor } from './components/CapabilityEditor';
import { TestingConsole } from './components/TestingConsole';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { useAuthStore } from '@stores/authStore';
import { canAdministerOrg } from '@/lib/roles';

// Same floating, rounded surface the rest of the themed screens use.
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]';

const PRIMARY =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border ' +
  'border-[var(--co-brass)] bg-[var(--co-brass)] px-3 text-[12.5px] font-semibold ' +
  'text-[var(--co-ink-invert)] hover:opacity-90';

/** Header-only variants: the header's controls take the Board's 10px radius. */
const HEADER_CONTROL = CONTROL.replace('rounded-lg', 'rounded-[10px]');
const HEADER_PRIMARY = PRIMARY.replace('rounded-lg', 'rounded-[10px]');

const CATEGORIES = [
  { id: 'all', label: 'All', icon: Grid },
  { id: 'ticket', label: 'Tickets', icon: Box },
  { id: 'customer', label: 'Customers', icon: Box },
  { id: 'order', label: 'Orders', icon: Package },
  { id: 'account', label: 'Account', icon: Settings },
  { id: 'knowledge', label: 'Knowledge', icon: FileCode },
  { id: 'custom', label: 'Custom', icon: Zap },
];

export function IntegrationCatalogPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  // Capabilities are organization configuration: everyone may browse them,
  // only an administrator may add, import, test, disable or edit them.
  const canManage = canAdministerOrg(useAuthStore((s) => s.user));
  // Delivery logs are per endpoint, on the tenant's Webhooks settings page.
  // Delivery logs live in Organization Settings, which only an admin may open.
  const deliveryLogs = tenantId && canManage ? `/settings/tenant/${tenantId}/webhooks` : null;
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingCapability, setEditingCapability] = useState<Capability | null>(null);
  const [testingCapability, setTestingCapability] = useState<Capability | null>(null);

  // Server-side pre-grouped catalog (mcp-server's GET /catalog) instead of
  // fetching the flat list and re-deriving groups client-side — the backend
  // already does this grouping, so no reason to duplicate it here.
  const { data: catalog, isLoading, error } = useCapabilityCatalog(
    selectedCategory !== 'all' ? selectedCategory : undefined
  );

  const deleteCapability = useDeleteCapability();
  const updateCapability = useUpdateCapability();

  // Filter within each category's array (the catalog endpoint doesn't take a
  // search term), dropping categories that end up empty so they don't render
  // as empty sections.
  const groupedCapabilities = useMemo(() => {
    const source = catalog?.capabilities;
    if (!source) return {} as Record<string, Capability[]>;

    const query = searchQuery.trim().toLowerCase();
    const result: Record<string, Capability[]> = {};

    for (const [category, caps] of Object.entries(source)) {
      const filtered = !query
        ? caps
        : caps.filter(
            (cap) =>
              cap.name.toLowerCase().includes(query) ||
              cap.description.toLowerCase().includes(query) ||
              cap.id.toLowerCase().includes(query) ||
              cap.tags.some((tag) => tag.toLowerCase().includes(query)),
          );
      if (filtered.length > 0) {
        result[category] = filtered;
      }
    }

    return result;
  }, [catalog, searchQuery]);

  const totalCount = Object.values(groupedCapabilities).reduce(
    (sum, caps) => sum + caps.length,
    0,
  );

  const handleToggleStatus = async (capability: Capability) => {
    const nextStatus = capability.status === 'active' ? 'disabled' : 'active';
    try {
      await updateCapability.mutateAsync({
        capabilityId: capability.id,
        data: { status: nextStatus },
      });
      toast.success(
        nextStatus === 'active' ? 'Capability enabled' : 'Capability disabled',
        capability.name,
      );
    } catch (err) {
      toast.error('Update failed', 'Failed to update capability status');
    }
  };

  const handleEdit = (capability: Capability) => {
    setEditingCapability(capability);
    setShowEditor(true);
  };

  const handleTest = (capability: Capability) => {
    setTestingCapability(capability);
  };

  const handleDelete = async (capability: Capability) => {
    if (!confirm(`Are you sure you want to delete "${capability.name}"?`)) {
      return;
    }

    try {
      await deleteCapability.mutateAsync(capability.id);
      toast.success('Capability deleted', `${capability.name} has been deleted`);
    } catch (err) {
      toast.error('Delete failed', 'Failed to delete capability');
    }
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingCapability(null);
  };

  const handleCreateNew = () => {
    setEditingCapability(null);
    setShowEditor(true);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-y-auto bg-[var(--co-canvas)] py-4 pr-4">
      <header className={`${CARD_CLASS} flex-none px-5 py-[18px]`} style={{ ...CARD, borderRadius: 14 }}>
        {/* Row 1: title + subtitle, the utility cluster in the top-right corner. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              Integration Catalog
            </h1>
            <p className="co-mono mt-[6px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              Manage AI capabilities and external integrations for your support platform
            </p>
          </div>
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: search + actions */}
        <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-[var(--co-ink-4)]" />
            <input
              type="text"
              placeholder="Search capabilities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-[34px] w-full rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] pl-9 pr-9 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-4)] focus:border-[var(--co-brass)] focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 z-10 -translate-y-1/2 text-[var(--co-ink-4)] hover:text-[var(--co-ink-2)]"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button type="button" onClick={() => navigate('/integrations/connectors')} className={HEADER_CONTROL}>
            <Activity className="h-[14px] w-[14px]" />
            Connectors
          </button>
          {canManage && (
            <>
              <button type="button" onClick={() => setShowImportWizard(true)} className={HEADER_CONTROL}>
                <Upload className="h-[14px] w-[14px]" />
                Import
              </button>
              <button type="button" onClick={handleCreateNew} className={HEADER_PRIMARY}>
                <Plus className="h-[14px] w-[14px]" />
                New Capability
              </button>
            </>
          )}
        </div>

        {/* Category segmented control */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                aria-pressed={isActive}
                className="flex h-[34px] items-center gap-[7px] whitespace-nowrap rounded-[10px] border px-3 text-[12.5px] transition-colors duration-[120ms] hover:border-[var(--co-brass)]"
                style={
                  isActive
                    ? {
                        borderColor: 'var(--co-brass-line)',
                        background: 'var(--co-brass-wash)',
                        color: 'var(--co-brass-ink)',
                        fontWeight: 600,
                      }
                    : {
                        borderColor: 'var(--co-line)',
                        background: 'var(--co-raised)',
                        color: 'var(--co-ink-2)',
                        fontWeight: 500,
                      }
                }
              >
                <Icon
                  className="h-[14px] w-[14px]"
                  style={{ color: isActive ? 'var(--co-brass-ink)' : 'var(--co-ink-3)' }}
                />
                {cat.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Content */}
      {isLoading ? (
        <div className={`${CARD_CLASS} flex flex-none justify-center px-5 py-12`} style={CARD}>
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className={`${CARD_CLASS} flex-none px-5 py-10`} style={CARD}>
          <p className="text-center text-[13px] text-[var(--co-burgundy)]">
            Failed to load capabilities. Please try again.
          </p>
        </div>
      ) : totalCount === 0 ? (
        <div className={`${CARD_CLASS} flex-none px-5 py-6`} style={CARD}>
          <EmptyState
            icon={<Package className="h-12 w-12" />}
            title="No capabilities found"
            description={
              searchQuery
                ? 'Try adjusting your search terms'
                : canManage
                  ? 'Get started by importing an API or creating a custom capability'
                  : 'An administrator adds capabilities here.'
            }
            action={
              !canManage ? undefined : (
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowImportWizard(true)} className={CONTROL}>
                  <Upload className="h-[14px] w-[14px]" />
                  Import API
                </button>
                <button type="button" onClick={handleCreateNew} className={PRIMARY}>
                  <Plus className="h-[14px] w-[14px]" />
                  Create Capability
                </button>
              </div>
              )
            }
          />
        </div>
      ) : (
        <div className="flex flex-none flex-col gap-[18px]">
          {Object.entries(groupedCapabilities).map(([category, caps]) => (
            <section key={category}>
              <div className="mb-[10px] flex items-center gap-2 px-1">
                <h2 className="co-mono text-[10.5px] font-semibold uppercase tracking-[.14em] text-[var(--co-ink-2)]">
                  {category}
                </h2>
                <span className="co-mono rounded-md border border-[var(--co-line-strong)] bg-[var(--co-selected)] px-[6px] py-[1px] text-[9.5px] font-semibold text-[var(--co-brass-ink)]">
                  {caps.length}
                </span>
              </div>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-[14px]">
                {caps.map((capability) => (
                  <CapabilityCard
                    key={capability.id}
                    capability={capability}
                    onEdit={() => handleEdit(capability)}
                    onTest={() => handleTest(capability)}
                    onDelete={() => handleDelete(capability)}
                    onToggleStatus={() => handleToggleStatus(capability)}
                    readOnly={!canManage}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <div
        className={`${CARD_CLASS} flex flex-none flex-wrap items-center justify-between gap-x-8 gap-y-3 px-[18px] py-[14px]`}
        style={CARD}
      >
        <p
          className="min-w-0 max-w-[110ch] flex-1 text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]"
          style={{ textWrap: 'pretty' } as React.CSSProperties}
        >
          Every card states{' '}
          <span className="text-[var(--co-brass-ink)]">when it last succeeded</span>, not just
          that it is connected.{' '}
          <strong className="font-semibold text-[var(--co-ink)]">
            A green badge with no timestamp is how a broken integration hides for a fortnight
          </strong>{' '}
          — so <code className="co-mono text-[11.5px] text-[var(--co-ink-2)]">last_success_at</code>{' '}
          is the one field on this screen worth adding.
        </p>
        {deliveryLogs && (
          <button
            type="button"
            onClick={() => navigate(deliveryLogs)}
            className="co-mono flex-none whitespace-nowrap text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)] hover:underline"
          >
            View all delivery logs →
          </button>
        )}
      </div>

      {/* Import Wizard Modal */}
      {showImportWizard && (
        <ImportWizard onClose={() => setShowImportWizard(false)} />
      )}

      {/* Capability Editor Modal */}
      {showEditor && (
        <CapabilityEditor
          capability={editingCapability}
          onClose={handleEditorClose}
        />
      )}

      {/* Testing Console Modal */}
      {testingCapability && (
        <TestingConsole
          capability={testingCapability}
          onClose={() => setTestingCapability(null)}
        />
      )}
    </div>
  );
}
